import { GrandAlliance } from '../models/IAlliance.js';
import { UnitType, UnitSuperType } from '../models/IUnit.js';
import { AbilityType, AbilitySuperType } from '../models/IAbility.js';
import { WeaponType, WeaponSelectionPer } from '../models/IWeapon.js';
import { ScourgeType } from '../models/IScourge.js';
import { PathRanks, PathSuperType } from '../models/ptg/IPath.js';
import { ModeOfPlay } from '../models/IBattlePack.js';
import { UpgradeSuperType, UpgradeType } from '../models/IUpgrade.js';
// The enhancement categories every army is expected to have. Stored as keys of
// the enhancements record so they're always present, even when empty.
export const ARTEFACTS_OF_POWER = 'Artefacts of Power';
export const HEROIC_TRAITS = 'Heroic Traits';
export const DEFAULT_ENHANCEMENT_CATEGORIES = [ARTEFACTS_OF_POWER, HEROIC_TRAITS];
// Render a requirement as the app's keyword-formatted restriction text (e.g.
// "**^^MONSTER^^** non-**^^UNIQUE^^** only"), so every table in the category can carry
// an identical, human-readable restriction derived from the one filter.
export function enhancementRestrictionText(req) {
    const parts = [];
    for (const kw of req.keywords)
        if (kw.trim())
            parts.push(`**^^${kw.trim()}^^**`);
    for (const kw of req.nonKeywords)
        if (kw.trim())
            parts.push(`non-**^^${kw.trim()}^^**`);
    return parts.length ? `${parts.join(' ')} only` : '';
}
// A globally-unique id. The unique part is a uuid (matching BSData, whose ids are
// uuids) with a Date.now()+random fallback for runtimes without crypto.randomUUID.
// An optional readable prefix is kept (e.g. "ability-<uuid>") — BSData itself
// prefixes some ids — and authored ids stay freely editable.
export function makeId(prefix) {
    const c = globalThis.crypto;
    const unique = c?.randomUUID
        ? c.randomUUID()
        : `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
    return prefix ? `${prefix}-${unique}` : unique;
}
function uid(prefix) {
    return makeId(prefix);
}
export function makeAbility(name) {
    return {
        id: uid('ability'),
        name,
        type: AbilityType.Activated,
        superType: AbilitySuperType,
        timing: '',
        keywords: [],
        effect: '',
        color: '',
        typeAttribute: '',
        declare: null,
        usedBy: null,
        cost: null,
        unlockCondition: null,
    };
}
export function makeWeapon(name) {
    return {
        id: uid('weapon'),
        name,
        type: WeaponType.Melee,
        range: null,
        attack: '',
        hit: '',
        wound: '',
        rend: '',
        damage: '',
        abilities: [],
    };
}
function emptyWeapons() {
    return { selected: {}, basic: {}, advanced: {} };
}
function makeModel() {
    return {
        id: uid('model'),
        name: '',
        min: 1,
        max: 1,
        weapons: emptyWeapons(),
        optionSets: [],
        baseSize: '',
    };
}
export function makeAuthoredUnit(name) {
    return {
        id: uid('unit'),
        name,
        type: UnitType.Infantry,
        superType: UnitSuperType,
        points: 0,
        health: '',
        save: '',
        move: null,
        control: null,
        ward: null,
        banishment: null,
        isUndersize: false,
        isWarmaster: false,
        isGeneral: false,
        canBeReinforced: false,
        isReinforced: false,
        models: [makeModel()],
        enhancements: {},
        abilities: [],
        keywords: [],
        optionSets: [],
        battleProfile: null,
        rules: [],
        _tags: [],
    };
}
export function makeUpgrade(name) {
    return {
        id: uid('upgrade'),
        name,
        type: 0,
        superType: UpgradeSuperType,
        points: 0,
        scourge: ScourgeType.None,
        abilities: [],
        rules: [],
    };
}
export function makeRegimentOfRenownUpgrade(name) {
    return {
        ...makeUpgrade(`Regiment of Renown: ${name}`),
        type: UpgradeType.RegimentOfRenown,
        superType: UpgradeSuperType,
    };
}
export function makePath(name) {
    return {
        id: uid('path'),
        name,
        type: 0,
        superType: PathSuperType,
        requirements: { keywords: [], nonKeywords: [] },
        aspiring: { name: PathRanks[0], options: [], selection: null },
        elite: { name: PathRanks[1], options: [], selection: null },
        mighty: { name: PathRanks[2], options: [], selection: null },
        legendary: { name: PathRanks[3], options: [], selection: null },
    };
}
export function makeRegimentOfRenownUnit(name) {
    return {
        unit: makeAuthoredUnit(name),
        count: 1,
        max: 1,
        points: 0,
    };
}
export function makeRegimentOfRenown(name) {
    return {
        id: uid('ror'),
        name,
        type: 0,
        superType: 'Other',
        points: 0,
        legends: false,
        modeOfPlay: ModeOfPlay.Any,
        selectableIn: [],
        sourceArmies: [],
        units: [],
        upgrades: [makeRegimentOfRenownUpgrade(name)],
    };
}
// An enhancement table (sub-group within a category): a named set of
// enhancement upgrades with an optional restriction (e.g. "Hero only").
export function makeEnhancementTable(name) {
    return { name, upgrades: [], restrictionText: '', scourge: ScourgeType.None };
}
// One regiment-composition rule: this leader may take min–max units matching the
// given keywords / sub-hero categories / unit names.
export function makeRegimentOption() {
    return { min: 0, max: 1, keywords: [], nonKeywords: [], subhero_categories: [], unit_names: [] };
}
// Materialize an authored ("override") battle profile, seeded from the unit's own
// warscroll fields. The merge keeps those duplicated fields in sync, so the author
// only needs to fill the list-construction fields the warscroll doesn't carry —
// chiefly regiment_options (composition), subhero_categories, and requiredLeader.
export function makeBattleProfile(unit) {
    return {
        name: unit?.name ?? '',
        unit_size: String(unit?.models?.[0]?.max ?? 1),
        points: unit?.points ?? 0,
        keywords: unit ? [...unit.keywords] : [],
        notes: [],
        reinforceable: unit?.canBeReinforced ?? false,
        subhero_categories: [],
        regiment_options: [],
        requiredLeader: '',
        undersizeCondition: '',
        retiringOn: '',
        exclusiveWith: '',
        legends: unit?.legends ?? false,
        hero: unit ? unit.type === UnitType.Hero : false,
        base_size: unit?.models?.[0]?.baseSize ?? '',
    };
}
// A unit's allowed-enhancement slot: marks a (hero) unit as eligible to take an
// enhancement table, keyed by the table's name. `slot` stays null until a list
// actually picks an upgrade into it. Every consumer gates eligibility on the name
// (the same key the army's enhancement tables use), so the id mirrors it — matching
// how BSData reuses one group id across all heroes that point at that table.
export function makeEnhancementSlot(name) {
    return { id: name, name, slot: null };
}
export function makeAuthoredArmy(name) {
    return {
        name,
        alliance: GrandAlliance.UNKNOWN,
        // The army keyword defaults to the army name — most armies' keyword is their
        // name (e.g. "Stormcast Eternals"). Authors can override it in settings.
        keyword: name,
        isArmyOfRenown: false,
        legends: false,
        units: [],
        battleFormations: [],
        battleTraits: [],
        lores: { spell: [], prayer: [], manifestation: [] },
        // Every army gets the expected enhancement categories up front (empty).
        enhancements: { [ARTEFACTS_OF_POWER]: [], [HEROIC_TRAITS]: [] },
        paths: [],
    };
}
// Flat weapon view: every basic selection wrapping a single weapon profile.
export function unitWeapons(unit) {
    const model = unit.models[0];
    if (!model)
        return [];
    return Object.values(model.weapons.basic).flatMap((sel) => sel.weapons);
}
export function withUnitWeapons(unit, weapons) {
    const model = unit.models[0] ?? makeModel();
    const basic = {};
    for (const w of weapons) {
        basic[w.name] = {
            // BSData gives the selection entry and its weapon profile distinct ids. The
            // profile keeps the canonical weapon id; the wrapper derives a distinct one.
            // (Derived, not minted, so it's stable across edits — and the wrapper id is
            // regenerated here on every save, so it must NOT be the field that round-trips
            // through `unitWeapons()`, which reads back the profiles, not the wrapper.)
            id: `${w.id}-selection`,
            name: w.name,
            min: 0,
            max: 0,
            per: WeaponSelectionPer.Unit,
            replaces: [],
            weapons: [w],
        };
    }
    const nextModel = { ...model, weapons: { selected: {}, basic, advanced: {} } };
    return { ...unit, models: [nextModel, ...unit.models.slice(1)] };
}
